import { useTranslation } from 'react-i18next';
import { formatUZS } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, User, CalendarClock, CreditCard } from 'lucide-react';

interface MurabahaContextBarProps {
  cartTotal: number;
  itemCount: number;
  customerName?: string | null;
  customerPhone?: string | null;
  selectedTerm?: number | null;
  monthlyPayment?: number | null;
  limitAmount?: number | null;
  limitExpireDate?: string | null;
  cardMasked?: string | null;
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Oqim davomida doim ko'rinib turadigan kontekst paneli */
export function MurabahaContextBar({
  cartTotal,
  itemCount,
  customerName,
  customerPhone,
  selectedTerm,
  monthlyPayment,
  limitAmount,
  limitExpireDate,
  cardMasked,
}: MurabahaContextBarProps) {
  const { t } = useTranslation();
  const expire = formatDate(limitExpireDate);
  const cardLast4 = cardMasked ? cardMasked.replace(/\D/g, '').slice(-4) : '';

  return (
    <div className="sticky top-0 z-20 -mx-4 mb-6 border-b border-border bg-background/95 px-4 py-3 backdrop-blur md:-mx-8 md:px-8">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <ShoppingCart size={16} className="text-muted-foreground" />
          <span className="font-semibold tabular-nums text-foreground">{formatUZS(cartTotal)}</span>
          <span className="text-muted-foreground">· {t('pos_context.items', { n: itemCount })}</span>
        </div>

        {!!customerName && (
          <div className="flex items-center gap-2">
            <User size={16} className="text-muted-foreground" />
            <span className="font-medium text-foreground">{customerName}</span>
            {customerPhone && customerPhone.length > 4 && (
              <span className="font-mono text-muted-foreground">{customerPhone}</span>
            )}
          </div>
        )}

        {!!selectedTerm && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">{t('pos_context.months', { n: selectedTerm })}</Badge>
            {!!monthlyPayment && (
              <span className="font-semibold tabular-nums text-primary">{t('pos_context.per_month', { amount: formatUZS(monthlyPayment) })}</span>
            )}
          </div>
        )}

        {!!limitAmount && (
          <div className="flex items-center gap-2">
            <CalendarClock size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">
              {t('pos_context.limit')} <span className="font-semibold tabular-nums text-foreground">{formatUZS(limitAmount)}</span>
              {expire && <> · {t('pos_context.valid_until')} <span className="font-medium text-foreground">{expire}</span></>}
            </span>
          </div>
        )}

        {!!cardLast4 && (
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">
              {t('pos_context.card')} <span className="font-mono text-foreground">•••• {cardLast4}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
